import dayjs from "dayjs"

import { Person } from "#classes/Person"
import {
  type CoreSkillLevel,
  type DigitalSkillLevel,
  type SculptureSkillLevel,
} from "#constants/skillLevelConstants"
import { OldStudentEvaluation } from "#customTypes/composedTypes/evaluationTypes"
import { type StudentTask } from "#customTypes/composedTypes/studentTaskTypes"
import {
  StudentArtwork,
  type StudentConstructor,
  type StudentForDisplay,
  type StudentForTable,
  StudentSkillHistory,
  StudentSkillType,
} from "#customTypes/composedTypes/studentTypes"
import { type Uuid } from "#customTypes/globalTypes"
import { type Address, type Attendance } from "#customTypes/types"
import { getNow, STUDENT_DOB_FORMAT } from "#utils/dateHelpers"
import {
  formatSkillLevelWithPending,
  getAllSkillLevelsAsOf,
  getAllSkillLevelsWithPending,
} from "#utils/studentSkillHelpers"

export class Student extends Person {
  id: Uuid
  firstName: string
  lastName: string
  name: string
  dob: string
  gender: string
  avatar: string
  /** Legacy; never read or written. Levels come from `skillHistory`. */
  coreSkillLevel?: CoreSkillLevel
  digitalSkillLevel?: DigitalSkillLevel
  sculptureSkillLevel?: SculptureSkillLevel
  personalPhone: string
  personalEmail: string
  guardian1Phone: string
  guardian1Email: string
  guardian2Phone?: string
  guardian2Email?: string
  primaryAddress: Address
  tasks?: StudentTask[]
  notes?: string
  tags?: string[]
  allergies?: string[]
  attendance?: Attendance
  assistantAttendance?: Attendance
  skillHistory?: StudentSkillHistory
  projects?: Record<Uuid, number>
  evals?: Record<Uuid, OldStudentEvaluation>
  artwork?: Record<Uuid, StudentArtwork>

  constructor(props: StudentConstructor) {
    super(props)
    this.id = props.id
    this.firstName = props.firstName
    this.lastName = props.lastName
    this.name = props.name ?? this.getName()
    this.dob = props.dob
    this.gender = props.gender
    this.avatar = props.avatar
    this.coreSkillLevel = props.coreSkillLevel
    this.digitalSkillLevel = props.digitalSkillLevel
    this.sculptureSkillLevel = props.sculptureSkillLevel
    this.personalPhone = props.personalPhone
    this.personalEmail = props.personalEmail
    this.guardian1Phone = props.guardian1Phone
    this.guardian1Email = props.guardian1Email
    this.guardian2Phone = props.guardian2Phone
    this.guardian2Email = props.guardian2Email
    this.primaryAddress = props.primaryAddress
    this.notes = props.notes
    this.tags = props.tags
    this.allergies = props.allergies
    // Normalize Firestore's explicit null to undefined
    this.attendance = props.attendance ?? undefined
    this.assistantAttendance = props.assistantAttendance ?? undefined
    this.tasks = props.tasks
    this.skillHistory = props.skillHistory
    this.projects = props.projects
    this.evals = props.evals
  }

  getDobString = (): string => {
    return dayjs(this.dob).format(STUDENT_DOB_FORMAT)
  }

  getNameAndDob = (): string => `${this.getDobString()} ${this.name}`

  getAge = (): number => {
    if (!this.dob) return 0
    return getNow().diff(dayjs(this.dob), "year")
  }

  /** Levels as of now, resolved from `skillHistory` (studentSkillHelpers). */
  getCurrentSkillLevels = () => getAllSkillLevelsAsOf(this)

  /**
   * Levels as of now, each with the promotion an approved eval has already
   * recorded for next semester.
   */
  getSkillLevelsWithPending = () => getAllSkillLevelsWithPending(this)

  /**
   * One cell for the students table: `"G → L, HD"`. The arrow marks a level
   * the student has been promoted to but has not started a class at yet.
   */
  getSkillLevels = (): string =>
    Object.values(this.getSkillLevelsWithPending())
      .map(formatSkillLevelWithPending)
      .filter(Boolean)
      .join(", ")

  getStudentForDisplay = (): StudentForDisplay => {
    const levels = this.getCurrentSkillLevels()

    return {
      id: this.id,
      avatar: this.avatar,
      name: this.name,
      dob: this.getDobString(),
      coreSkillLevel: levels[StudentSkillType.core],
      digitalSkillLevel: levels[StudentSkillType.digital],
      sculptureSkillLevel: levels[StudentSkillType.sculpture],
      allergies: this.getAllergiesAsText(),
      tags: this.getTagsAsText(),
    }
  }

  getStudentForTable = (): StudentForTable => {
    return {
      id: this.id,
      name: this.name,
      dob: dayjs(this.dob).format(STUDENT_DOB_FORMAT),
      gender: this.gender,
      skillLevels: this.getSkillLevels(),
      personalPhone: this.personalPhone,
      personalEmail: this.personalEmail,
      guardian1Phone: this.guardian1Phone,
      guardian1Email: this.guardian1Email,
      guardian2Phone: this.guardian2Phone ?? "",
      guardian2Email: this.guardian2Email ?? "",
      primaryAddress: this.getFormattedAddress(),
      notes: this.notes ?? "",
    }
  }

  // toConstructor = (): StudentConstructor => ({
  //   id: this.id,
  //   firstName: this.firstName,
  //   lastName: this.lastName,
  //   name: this.name,
  //   dob: this.dob,
  //   gender: this.gender,
  //   personalPhone: this.personalPhone,
  //   personalEmail: this.personalEmail,
  //   guardian1Phone: this.guardian1Phone,
  //   guardian1Email: this.guardian1Email,
  //   guardian2Phone: this.guardian2Phone,
  //   guardian2Email: this.guardian2Email,
  //   primaryAddress: this.primaryAddress,
  //   notes: this.notes,
  //   tags: this.tags,
  //   allergies: this.allergies,
  //   attendance: this.attendance,
  // })
}
